import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, PlayCircle, FileText, ClipboardCheck, Lock, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import UpgradeModal from "../components/UpgradeModal";

export default function CourseDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [completed, setCompleted] = useState(new Set());
  const [attempt, setAttempt] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("module");

  const paid = !!user && user.plan && user.plan !== "free";

  const load = async () => {
    const [{ data: d }, { data: prog }] = await Promise.all([
      api.get(`/courses/${courseId}`),
      api.get(`/progress`),
    ]);
    setData(d);
    setSelected(d.lessons?.[0] || null);
    const set = new Set(prog.lessons_completed.filter((p) => p.course_id === courseId).map((p) => p.lesson_id));
    setCompleted(set);
    const best = prog.attempts
      .filter((a) => a.course_id === courseId)
      .sort((a, b) => b.score - a.score)[0];
    setAttempt(best || null);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const markComplete = async (lessonId) => {
    try {
      await api.post("/lessons/complete", { lesson_id: lessonId });
      setCompleted(new Set([...completed, lessonId]));
    } catch (e) {
      if (e.response?.status === 402) {
        setUpgradeReason("module");
        setShowUpgrade(true);
      }
    }
  };

  if (!data) {
    return <div className="p-10 text-slate-500">Loading…</div>;
  }
  const { course, lessons, quiz } = data;
  const courseLocked = !!course.locked;
  const lessonCompletedCount = lessons.filter((l) => completed.has(l.lesson_id)).length;
  const allLessonsComplete = lessonCompletedCount === lessons.length;

  // Course-level paywall (free user + non-preview module)
  if (courseLocked) {
    return (
      <div className="p-6 lg:p-10 max-w-4xl" data-testid="course-locked-page">
        <Link to="/courses" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-orange-600">
          <ArrowLeft className="w-4 h-4" /> All modules
        </Link>
        <div className="mt-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">
          Module {String(course.order).padStart(2, "0")} · Locked
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950 mt-1">{course.title}</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">{course.description}</p>

        <div className="mt-8 border border-slate-200 rounded-sm p-8 bg-slate-50 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-950 grid place-items-center rounded-sm text-white">
                <Lock className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Premium module</div>
                <div className="font-display font-bold text-xl text-slate-950">Available on any paid plan</div>
              </div>
            </div>
            <p className="text-slate-700 mt-4">
              This module is part of the Platinum 1 certification curriculum. Upgrade once and unlock all 8
              modules, every certification quiz, and your downloadable PDF credential. 3-day money-back guarantee.
            </p>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-800">
              {[
                "Full lesson library + videos",
                "Certification quizzes (auto-graded)",
                "PDF Platinum 1 Certificate",
                "Lifetime access · 3-day refund",
              ].map((f) => (
                <li key={f} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-orange-600" />{f}</li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-4">
            <Link
              to="/pricing"
              data-testid="locked-upgrade-btn"
              className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-sm font-semibold inline-flex items-center justify-center gap-2"
            >
              <Sparkles className="w-4 h-4" /> See plans
            </Link>
            <Link to="/courses" className="mt-2 w-full text-center border border-slate-300 hover:bg-white py-3 rounded-sm font-semibold text-slate-900 inline-block">
              Try free modules
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-7xl" data-testid="course-detail-page">
      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} reason={upgradeReason} />

      <Link to="/courses" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-orange-600">
        <ArrowLeft className="w-4 h-4" /> All modules
      </Link>
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-4">
          <div className="flex items-center gap-2">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">
              Module {String(course.order).padStart(2, "0")}
            </div>
            {course.is_preview && (
              <span className="text-xs font-bold uppercase tracking-[0.15em] bg-orange-600 text-white px-2 py-0.5 rounded-sm">Free</span>
            )}
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-950 mt-1">{course.title}</h1>
          <p className="text-slate-600 mt-3">{course.description}</p>

          <div className="mt-6 border border-slate-200 rounded-sm divide-y divide-slate-200">
            {lessons.map((l, idx) => {
              const done = completed.has(l.lesson_id);
              const active = selected?.lesson_id === l.lesson_id;
              return (
                <button
                  key={l.lesson_id}
                  onClick={() => setSelected(l)}
                  data-testid={`lesson-item-${idx}`}
                  className={`w-full text-left p-4 flex items-start gap-3 hover:bg-slate-50 transition-colors ${active ? "bg-orange-50" : ""}`}
                >
                  <div className={`w-7 h-7 grid place-items-center rounded-sm shrink-0 ${done ? "bg-green-600 text-white" : "bg-slate-100 text-slate-700"}`}>
                    {done ? <CheckCircle2 className="w-4 h-4" /> : <span className="text-xs font-bold">{idx + 1}</span>}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-900 leading-snug">{l.title}</div>
                    <div className="text-xs text-slate-500 mt-1">{l.duration_min} min · {l.video_url ? "Video + Reading" : "Reading"}</div>
                  </div>
                </button>
              );
            })}
            {quiz && (
              <div className="p-4 bg-slate-950 text-white">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-orange-400 flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4" /> Certification quiz
                </div>
                <div className="font-display font-bold mt-2">{quiz.title}</div>
                <div className="text-xs text-slate-400 mt-1">{quiz.num_questions} questions · Pass {quiz.pass_score}%</div>
                {attempt && (
                  <div className="mt-2 text-xs">
                    Best score: <span className={`font-bold ${attempt.passed ? "text-green-400" : "text-orange-400"}`}>{attempt.score}%</span>
                  </div>
                )}
                {quiz.locked ? (
                  <>
                    <button
                      data-testid="start-quiz-locked-btn"
                      onClick={() => { setUpgradeReason("quiz"); setShowUpgrade(true); }}
                      className="mt-3 w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2.5 rounded-sm inline-flex items-center justify-center gap-2"
                    >
                      <Lock className="w-4 h-4" /> Unlock quiz
                    </button>
                    <div className="text-xs text-slate-400 mt-2">Included in every paid plan.</div>
                  </>
                ) : (
                  <>
                    <button
                      data-testid="start-quiz-btn"
                      disabled={!allLessonsComplete && !attempt}
                      onClick={() => navigate(`/quizzes/${quiz.quiz_id}`)}
                      className="mt-3 w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-sm inline-flex items-center justify-center gap-2"
                      title={allLessonsComplete ? "" : "Complete all lessons first"}
                    >
                      {attempt ? "Retake quiz" : "Take quiz"}
                    </button>
                    {!allLessonsComplete && !attempt && (
                      <div className="text-xs text-slate-400 mt-2">Finish all lessons to unlock.</div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </aside>

        <section className="lg:col-span-8">
          {selected && (
            <div>
              {selected.video_url ? (
                <div className="aspect-video w-full bg-slate-950 rounded-sm overflow-hidden">
                  <iframe
                    src={selected.video_url}
                    title={selected.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="aspect-video bg-slate-100 rounded-sm grid place-items-center">
                  <div className="text-center">
                    <FileText className="w-10 h-10 text-slate-400 mx-auto" />
                    <div className="text-slate-500 mt-2 text-sm">Reading-only lesson</div>
                  </div>
                </div>
              )}
              <div className="mt-6 flex items-center gap-2">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Lesson</div>
                <div className="text-xs text-slate-400">·</div>
                <div className="text-xs text-slate-500">{selected.duration_min} min</div>
              </div>
              <h2 className="font-display text-3xl font-bold tracking-tight text-slate-950 mt-2">{selected.title}</h2>
              <p className="mt-4 text-slate-700 leading-relaxed text-base whitespace-pre-line">{selected.content}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                {completed.has(selected.lesson_id) ? (
                  <div className="inline-flex items-center gap-2 px-4 py-2.5 text-green-700 bg-green-50 border border-green-200 rounded-sm font-semibold text-sm">
                    <CheckCircle2 className="w-4 h-4" /> Lesson complete
                  </div>
                ) : (
                  <button
                    onClick={() => markComplete(selected.lesson_id)}
                    data-testid="mark-complete-btn"
                    className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 rounded-sm font-semibold inline-flex items-center gap-2"
                  >
                    <PlayCircle className="w-4 h-4" /> Mark as complete
                  </button>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
